import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logout as logoutAction, setToken, fetchCurrentUser } from '../slices/authSlice';

const AuthContext = createContext();

// Creamos un contexto de navegación separado
const NavigationContext = createContext();

// Este componente separado maneja la navegación después de la autenticación
export const NavigationProvider = ({ children }) => {
  // Este componente debe ser usado dentro de un Router
  return (
    <NavigationContext.Provider value={{}}>
      {children}
    </NavigationContext.Provider>
  );
};

// Principal proveedor de autenticación
export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { user, token } = useSelector((state) => state.auth);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Initialize auth state from sessionStorage (3 ago 2026: ver
    // services/api.js -- el token vive en sessionStorage a propósito, no en
    // localStorage, para que cerrar el navegador cierre la sesión).
    const storedToken = sessionStorage.getItem('token');
    if (storedToken) {
      dispatch(setToken(storedToken));
      // Traer el usuario real del backend. Antes esto no se hacía y
      // `user` quedaba en null después de cualquier recarga — el avatar
      // de la barra lateral siempre mostraba "U" en vez de la inicial
      // real, sin importar cuántas veces se hubiera arreglado el token.
      dispatch(fetchCurrentUser()).finally(() => setIsInitialized(true));
    } else {
      setIsInitialized(true); // También marcamos como inicializado si no hay token
    }
  }, [dispatch]);

  // La función logout ahora no depende de useNavigate
  const logout = useCallback(() => {
    dispatch(logoutAction());
    // La navegación se manejará en el componente que use este hook
  }, [dispatch]);

  // 7 sep 2026: mismo fix que LanguageContext.js -- memoizar el value
  // para no forzar un re-render de TODOS los consumidores de useAuth()
  // (NotionSidebar, NotionHeader, PrivateRoute, etc.) en cada render de
  // AuthProvider, solo cuando alguno de estos datos realmente cambia.
  const value = useMemo(
    () => ({ user, token, logout, isInitialized }),
    [user, token, logout, isInitialized]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
